// frontend/src/modules/invoicing/pages/ClientsPage.tsx
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Users, Search, Mail, Phone, ArrowUpRight, Upload, ArchiveRestore } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { useApi } from '../../../design-system/useApi';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { formatDate } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { ImportCsvDialog } from '../components/ImportCsvDialog';
import { clientsApi } from '../api';
import type { Client } from '../types';

const CLIENT_IMPORT_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'billing_address', label: 'Billing address' },
  { key: 'tax_id', label: 'Tax ID' },
  { key: 'notes', label: 'Notes' },
];

function avatarBgFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `oklch(0.88 0.07 ${h})`;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ClientsPage() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = React.useState(false);
  const { data, loading, reload } = useApi<{ clients: Client[] }>(
    `/api/invoicing/clients${showArchived ? '?include_archived=true' : ''}`,
  );
  const [query, setQuery] = React.useState('');
  const [importOpen, setImportOpen] = React.useState(false);
  const [restoringId, setRestoringId] = React.useState<string | null>(null);

  const all = data?.clients ?? [];
  const rows = React.useMemo(() => {
    if (!query.trim()) return all;
    const q = query.trim().toLowerCase();
    return all.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.email ?? '').toLowerCase().includes(q) ||
      (c.phone ?? '').toLowerCase().includes(q),
    );
  }, [all, query]);

  async function unarchive(c: Client) {
    setRestoringId(c.id);
    try {
      await clientsApi.update(c.id, { status: 'active' });
      toast.success(`${c.name} restored`);
      reload();
    } catch (err: any) {
      toast.error('Unarchive failed', { description: err.message });
    } finally {
      setRestoringId(null);
    }
  }

  const withEmail = all.filter((c) => !!c.email).length;
  const withPhone = all.filter((c) => !!c.phone).length;

  return (
    <div>
      <PageHeader
        eyebrow="Money / Clients"
        title="Clients"
        description="People and companies you bill."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Upload className="h-3.5 w-3.5" />
              Import CSV
            </Button>
            <Link to="/dashboard/clients/new">
              <Button data-tour="clients.new" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                New client
              </Button>
            </Link>
          </div>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi label="Total" value={all.length} caption="On the books" icon={<Users className="h-3.5 w-3.5" />} />
          <Kpi label="With email" value={withEmail} caption="Reachable" icon={<Mail className="h-3.5 w-3.5" />} tone="saffron" />
          <Kpi label="With phone" value={withPhone} caption="Reachable" icon={<Phone className="h-3.5 w-3.5" />} tone="muted" />
        </section>

        <section className="flex flex-wrap items-center gap-4">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="search"
              placeholder="Search name, email, or phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 pl-9 rounded-full bg-card/80 border-border/60 text-[13px]"
            />
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-saffron"
            />
            <span className="text-[12.5px] font-semibold text-muted-foreground">Show archived</span>
          </label>
        </section>

        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          {loading ? (
            <ListSkeleton />
          ) : rows.length === 0 ? (
            <EmptyClients filtered={!!query} />
          ) : (
            <ul className="divide-y divide-border/40">
              {rows.map((c, i) => (
                <motion.li
                  key={c.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.02, 0.18) }}
                >
                  <div className={['flex items-center hover:bg-muted/40 transition-colors', c.status === 'archived' ? 'opacity-75' : ''].join(' ')}>
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/clients/${c.id}`)}
                      className="flex-1 min-w-0 text-left flex items-center gap-4 px-5 sm:px-6 py-4"
                    >
                      <div
                        className="h-11 w-11 rounded-2xl ring-1 ring-border/40 flex items-center justify-center text-[13px] font-bold text-foreground/85 shrink-0"
                        style={{ background: avatarBgFor(c.name) }}
                        aria-hidden
                      >
                        {initialsFor(c.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{c.name}</p>
                        <p className="text-[12px] text-muted-foreground mt-0.5 truncate">
                          {c.email ?? '(no email)'}
                          {c.phone ? ` · ${c.phone}` : ''}
                        </p>
                      </div>
                      {c.currency && (
                        <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full bg-muted/60 ring-1 ring-border/40 text-[10.5px] font-bold tracking-[0.08em] text-muted-foreground">
                          {c.currency}
                        </span>
                      )}
                      <span className="text-[11.5px] text-muted-foreground/80 hidden sm:inline">
                        Added {formatDate(c.created_at)}
                      </span>
                    </button>
                    {c.status === 'archived' && (
                      <div className="flex items-center gap-2 pr-5 sm:pr-6 shrink-0">
                        <span className={statusPill('muted')}><span className={statusDot('muted')} />Archived</span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={restoringId === c.id}
                          onClick={() => unarchive(c)}
                          className="rounded-full gap-1.5 text-[12px] font-semibold"
                        >
                          <ArchiveRestore className="h-3.5 w-3.5" />
                          {restoringId === c.id ? 'Restoring…' : 'Unarchive'}
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <ImportCsvDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import clients from CSV"
        description="Upload a CSV with a header row. We'll preview it, then add each valid row as a client."
        fields={CLIENT_IMPORT_FIELDS}
        onImport={(rows) => clientsApi.importRows(rows)}
        onImported={reload}
      />
    </div>
  );
}

function Kpi({ label, value, caption, icon, tone = 'neutral' }: { label: string; value: number; caption: string; icon: React.ReactNode; tone?: 'neutral' | 'saffron' | 'muted' }) {
  const toneClass =
    tone === 'saffron' ? 'bg-saffron/15 text-saffron-foreground ring-saffron/30' :
    tone === 'muted'   ? 'bg-muted text-muted-foreground ring-border/50' :
                         'bg-muted text-muted-foreground ring-border/50';
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span className={['inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1', toneClass].join(' ')}>{icon}</span>
      </div>
      <p className="font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-border/40">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-11 w-11 rounded-2xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyClients({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return (
      <div className="py-14 px-8 text-center">
        <p className="text-[14px] font-semibold text-foreground">No matching clients</p>
        <p className="text-[13px] text-muted-foreground mt-1">Try a different search.</p>
      </div>
    );
  }
  return (
    <div className="py-14 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
        <Users className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
      </div>
      <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">Add your first client</p>
      <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
        Clients are who you bill. Add one once, reuse on every invoice.
      </p>
      <div className="mt-6">
        <Link to="/dashboard/clients/new">
          <Button className="rounded-full h-10 px-5 font-semibold gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New client <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
